import sortBy from 'lodash/sortBy';

import { getOrCreate } from 'src/api/greenhouse/HydrateUtils';
import { Department, Job, Office, TreeNode } from 'src/api/greenhouse/hydratedTypes.greenhouse';

import { OfficeJson } from './contentTypes.greenhouse';
import { HydratedOffices, hydrateOffices } from './hydrateOffices';

export const CATCH_ALL_ID = 0;

function calculateLevel(category: TreeNode) {
  let node: TreeNode | undefined = category;
  let level = -1;
  while (node) {
    node = node.parent;
    level++;
  }
  return level;
}

function buildSortLabel(category: TreeNode) {
  const path = [];
  let node: TreeNode | undefined = category;

  while (node) {
    path.unshift(node.name);
    node = node.parent;
  }

  return path.filter(s => !!s).join(' - ');
}

export type CategoryOption = Readonly<ReturnType<typeof toCategoryOption>>;
export function toCategoryOption(category: TreeNode) {
  const { id, name, children } = category;

  return {
    value: id,
    sortLabel: buildSortLabel(category),
    label: name,
    isParent: children.size > 0,
    level: calculateLevel(category),
  };
}

export function toCategoryOptions<T extends TreeNode>(store: Map<number, T>): ReadonlyArray<CategoryOption> {
  const options = Array.from(store.values()).filter(value => value.id !== CATCH_ALL_ID).map(toCategoryOption);

  return sortBy(options, job => job.sortLabel);
}

export type JobListing = Readonly<{
  id: number;
  title: string;
  url: string;
  location: string;
}>;

export type Category = Readonly<{
  id: number;
  isParent: boolean;
  name: string;
  sortLabel: string;
  jobs: ReadonlyArray<JobListing>;
}>;

export type JobFilter = {
  noDepartment?: Department;
  selectedDepartment?: Department;
  noOffice?: Office;
  selectedOffice?: Office;
};

export function has(job: Job, selected?: TreeNode, all?: TreeNode): boolean {
  // if selected exists, check if the job belongs to that selected or all
  return !selected || selected.jobs.has(job) || !!all && all.jobs.has(job);
}

export function shouldInclude(job: Job, filter: JobFilter): boolean {
  const { noDepartment, selectedDepartment, noOffice, selectedOffice } = filter;
  return has(job, selectedOffice, noOffice) && has(job, selectedDepartment, noDepartment);
}

export function categorize(jobs: Job[]): Map<number, Category> {
  return jobs.reduce((categories, job) => {
    const { id: jobId, title, locationName: location, absolute_url: url, department } = job;
    const { id: departmentId, parent, name } = department;

    const category = getOrCreate(categories, departmentId, () => ({
      id: departmentId,
      isParent: !parent,
      name,
      sortLabel: buildSortLabel(department),
      jobs: [],
    }));

    (category.jobs as JobListing[]).push({ id: jobId, title, url, location });

    return categories;
  }, new Map<number, Category>());
}

export type JobBoard = ReturnType<typeof process>;
export function process({ offices, departments, jobs }: HydratedOffices) {
  // Special no department and no office
  const noDepartment = departments.get(CATCH_ALL_ID);
  const noOffice = offices.get(CATCH_ALL_ID);

  // handy filter function
  function filter(departmentId = -1, officeId = -1): ReadonlyArray<Category> {
    const selectedDepartment = departments.get(departmentId);
    const selectedOffice = offices.get(officeId);

    const jobFilter = { noDepartment, selectedDepartment, noOffice, selectedOffice };

    const filtered = Array.from(jobs.values()).filter(job => shouldInclude(job, jobFilter));

    const categorized = categorize(filtered);

    const categories = Array.from(categorized.values()).map(category => ({
      ...category,
      jobs: sortBy(category.jobs, job => job.title, job => job.location),
    }));

    return sortBy(categories, category => category.sortLabel);
  }

  return {
    offices: toCategoryOptions(offices),
    departments: toCategoryOptions(departments),
    filter,
  };
}

export function toJobBoard(json: OfficeJson[]) {
  return process(hydrateOffices(json));
}
